// ===========================================
// Event Editor Page (Create / Edit)
// ===========================================
// Full-page form for creating a new event or editing an existing one.
// Routes: events/new  and  events/:id/edit

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  InputAdornment,
  Divider,
  Chip,
  Collapse,
  Autocomplete,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  CloudUpload as UploadIcon,
  Image as PixabayIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AppLocalizationProvider } from '../../components/common/AppLocalizationProvider';
import { useSnackbar } from 'notistack';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  useEvent,
  useCreateEvent,
  useUpdateEvent,
  useEventSettings,
} from '../../hooks/useEvents';
import { usePermissions } from '../../hooks/usePermissions';
import type { BookingFeeMode, CheckoutWords } from '../../api/events.api';
import { titleCaseWords } from '../../utils/checkoutWords';
import { apiErrorCode } from '../../utils/apiError';
import {
  bookingFeeAmountError,
  bookingFeeChoiceOf,
  bookingFeeOverridesFor,
  bookingFeeUnitText,
  type BookingFeeChoice,
} from '../../utils/bookingFee';
import { formatCents } from '../../components/events/EventTierPricing';
import {
  EventType,
  EventVisibility,
  CreateEventData,
} from '../../api/events.api';
import { eventApi } from '../../api/events.api';
import { useFamilies } from '../../hooks/useUsers';
import { useLessons } from '../../hooks/useLessons';
import { useAgeGroups, useProgramLevels } from '../../hooks/useAdmin';
import EventPixabayPicker from '../../components/admin/EventPixabayPicker';
import EventVenueSelector, {
  VenueSelection,
  EMPTY_VENUE_SELECTION,
} from '../../components/events/EventVenueSelector';
import { useAuth } from '../../contexts/useAuth';
import {
  STANDARD_TICKET_TIERS,
  resolveTierLabel,
  SCHOOL_NAME_TOKEN,
} from '../../constants/eventTicketTiers';
import { useEventPricingStructures } from '../../hooks/useAdmin';
import type { EventPricingStructure } from '../../api/admin.api';
import { toLocalDateString, localDateFromKey } from '../../utils/dateUtils';

// ===========================================
// OPTION LISTS
// ===========================================

// Values MUST match the backend EventType enum (z.nativeEnum validation).
const eventTypeOptions: { value: EventType; label: string }[] = [
  { value: 'CONCERT', label: 'Concert' },
  { value: 'RECITAL', label: 'Recital' },
  { value: 'WORKSHOP', label: 'Workshop' },
  { value: 'OPEN_HOUSE', label: 'Open House' },
  { value: 'MASTERCLASS', label: 'Masterclass' },
  { value: 'REHEARSAL', label: 'Rehearsal' },
  { value: 'RECORDING_SESSION', label: 'Recording Session' },
  { value: 'COMMUNITY_SERVICE', label: 'Community Service' },
  { value: 'PARENT_NIGHT', label: 'Parent Night' },
  { value: 'HOLIDAY_CAMP', label: 'Holiday Camp' },
  { value: 'OTHER', label: 'Other' },
];

// Values MUST match the backend EventVisibility enum (z.nativeEnum validation).
const visibilityOptions: { value: EventVisibility; label: string }[] = [
  { value: 'ALL_FAMILIES', label: 'All Families' },
  { value: 'SPECIFIC_FAMILIES', label: 'Specific Families' },
  { value: 'SPECIFIC_CLASSES', label: 'Specific Classes' },
  { value: 'SPECIFIC_AGE_GROUPS', label: 'Specific Age Groups' },
  { value: 'SPECIFIC_PROGRAM_LEVELS', label: 'Specific Program Levels' },
  { value: 'STAFF_ONLY', label: 'Staff Only' },
  { value: 'PUBLIC', label: 'Public' },
];

// ===========================================
// OPTION SHAPES
// ===========================================

interface TargetOption {
  id: string;
  label: string;
}

// ===========================================
// FORM STATE TYPE
// ===========================================

interface FormState {
  title: string;
  description: string;
  type: EventType;
  visibility: EventVisibility;
  startDateTime: Date | null;
  endDateTime: Date | null;
  capacity: string;
  registrationDeadline: Date | null;
  isFree: boolean;
  /**
   * One row per offered tier. Replaces the old `Record<key, dollars>` map so a
   * tier can carry its own expiry day. `availableUntil` is a LOCAL-midnight Date
   * (built with `localDateFromKey`) and goes back out via `toLocalDateString` —
   * `.toISOString()` shifts the day west of UTC.
   */
  tierRows: TierRow[];
  /**
   * The pricing structure last used to SEED these rows in THIS editing session (null for a loaded event).
   * Sent as `pricingStructureId` (events B4, #311) so the server takes the tier labels from that structure
   * when two structures share a key. It is not stored on the event: structures seed, they never bind.
   */
  pricingStructureId: string | null;
  /** Which of the school's two checkout wordings: '' = from the event type (events B2, #680). */
  checkoutWording: '' | 'TICKET' | 'PLACE';
  /** This event's booking fee (events B3, #681): the school's setting, none, or its own mode and amount. */
  bookingFeeChoice: BookingFeeChoice;
  /** Dollars, typed by the admin; only read when bookingFeeChoice is CUSTOM. */
  bookingFeeDollars: string;
  /** The custom fee's mode: '' = follow the school's mode. Only read when bookingFeeChoice is CUSTOM. */
  bookingFeeCustomMode: '' | BookingFeeMode;
  requiresApproval: boolean;
  featuredImage: string;
  announcementScheduledFor: Date | null;
}

/** One editable ticket-tier row in the pricing matrix. */
export interface TierRow {
  key: string;
  label: string;
  priceDollars: string;
  /**
   * Whether this tier stops selling on a day. Held SEPARATELY from the day so
   * the admin can switch it on and then pick — `availableUntil` alone cannot
   * express "on, not yet chosen", which is exactly the state the toggle needs.
   */
  hasEndDate: boolean;
  availableUntil: Date | null;
}

/** The 7 standard tiers, blank — the pre-Phase-G default when no structure is picked. */
const emptyTierRows = (schoolName: string): TierRow[] =>
  STANDARD_TICKET_TIERS.map((t) => ({
    key: t.key,
    label: resolveTierLabel(t.key, schoolName),
    priceDollars: '',
    hasEndDate: false,
    availableUntil: null,
  }));

/**
 * Rows seeded from a school-defined pricing structure (D3: picking a structure
 * REPLACES the matrix, never merges).
 *
 * ⚠️ A structure carries `hasEndDate`, never a DATE (Andrew, 2026-09-15). An early-bird
 * cutoff depends on the EVENT's own date — next March's concert closes on a
 * different day from this December's — so a seeded row always starts with its
 * expiry OFF and the admin sets the day here.
 */
const rowsFromStructure = (
  structure: EventPricingStructure,
  schoolName: string
): TierRow[] =>
  [...structure.Tiers]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((t) => ({
      key: t.key,
      label: t.label.replaceAll(SCHOOL_NAME_TOKEN, schoolName),
      priceDollars: t.defaultPriceCents > 0 ? String(t.defaultPriceCents / 100) : '',
      // ⚠️ FROM the structure, never hardcoded. The STRUCTURE declares which
      // tiers expire ("Early Bird does, Adult does not"); the EVENT supplies the
      // day, because that day depends on the event's own date. (Andrew,
      // 2026-09-15 — this corrects the Phase G revision, which removed the
      // concept from the structure instead of swapping the date for a flag.)
      hasEndDate: t.hasEndDate,
      availableUntil: null,
    }));

/** "Buy Tickets" / "Book Places": the school's own words in the Wording picker. */
const wordingOptionLabel = (w: CheckoutWords | undefined, fallback: string): string =>
  w ? `${w.verb} ${titleCaseWords(w.plural)}` : fallback;

const defaultForm: FormState = {
  title: '',
  description: '',
  type: 'OTHER',
  visibility: 'PUBLIC',
  startDateTime: null,
  endDateTime: null,
  capacity: '',
  registrationDeadline: null,
  isFree: true,
  tierRows: [],
  pricingStructureId: null,
  checkoutWording: '',
  bookingFeeChoice: 'SCHOOL',
  bookingFeeDollars: '',
  bookingFeeCustomMode: '',
  requiresApproval: false,
  featuredImage: '',
  announcementScheduledFor: null,
};

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function EventEditorPage() {
  const navigate = useNavigate();
  const { schoolSlug, id } = useParams<{ schoolSlug: string; id: string }>();
  const isEditing = !!id;
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const schoolName = user?.schoolName ?? '';
  const { data: pricingStructures } = useEventPricingStructures();
  // The school's own words, for the Wording picker (a label only: the server resolves the words).
  const { can } = usePermissions();
  const { data: eventSettings } = useEventSettings(can('events', 'read'));
  // D3: picking a structure REPLACES the matrix. When prices are already typed we
  // confirm first, so a structure change can never silently wipe the admin's work.
  const [pendingStructureId, setPendingStructureId] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>(defaultForm);
  const [venue, setVenue] = useState<VenueSelection>(EMPTY_VENUE_SELECTION);
  const [targetFamilies, setTargetFamilies] = useState<string[]>([]);
  const [targetClasses, setTargetClasses] = useState<string[]>([]);
  const [targetAgeGroups, setTargetAgeGroups] = useState<string[]>([]);
  const [targetProgramLevels, setTargetProgramLevels] = useState<string[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Featured image upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showPixabayPicker, setShowPixabayPicker] = useState(false);

  // Load existing event when editing
  const { data: event, isLoading: isLoadingEvent } = useEvent(id ?? '');

  // Data for target pickers
  const { data: familiesData, isLoading: isLoadingFamilies } = useFamilies();
  const { data: lessonsData, isLoading: isLoadingLessons } = useLessons();
  const { data: ageGroupsData, isLoading: isLoadingAgeGroups } = useAgeGroups();
  const { data: programLevelsData, isLoading: isLoadingProgramLevels } = useProgramLevels();

  // Map to { id, label } option objects
  const familyOptions: TargetOption[] = (familiesData ?? []).map((f) => ({
    id: f.id,
    label: f.name,
  }));

  const classOptions: TargetOption[] = (lessonsData ?? []).map((l) => ({
    id: l.id,
    label: l.name,
  }));

  const ageGroupOptions: TargetOption[] = (ageGroupsData ?? []).map((ag) => ({
    id: ag.id,
    label: ag.name,
  }));

  const programLevelOptions: TargetOption[] = (programLevelsData ?? []).map((pl) => ({
    id: pl.id,
    label: pl.displayName ?? pl.name,
  }));

  // A NEW event starts with the 7 standard tiers, blank — the pre-Phase-G
  // behaviour. Guarded on `length === 0` so it never clobbers rows the admin has
  // typed or seeded from a structure, and keyed on schoolName because {SCHOOL}
  // resolves after the first render.
  //
  // ⚠️ The safety here lives in `ProtectedRoute` (App.tsx), which holds a spinner
  // until auth resolves — so this page cannot mount with `user === null` and the
  // `length === 0` guard can never lock in rows labelled " Students". If that
  // gate is ever removed, this effect becomes wrong: once rows exist the
  // schoolName dependency can no longer repair their labels.
  useEffect(() => {
    if (isEditing) return;
    setForm((prev) =>
      prev.tierRows.length === 0
        ? { ...prev, tierRows: emptyTierRows(schoolName) }
        : prev
    );
  }, [isEditing, schoolName]);

  useEffect(() => {
    if (event) {
      setForm({
        title: event.title,
        description: event.description ?? '',
        type: event.type,
        visibility: event.visibility,
        startDateTime: new Date(event.startDateTime),
        endDateTime: new Date(event.endDateTime),
        capacity: event.capacity != null ? String(event.capacity) : '',
        registrationDeadline: event.registrationDeadline
          ? new Date(event.registrationDeadline)
          : null,
        isFree: event.isFree,
        tierRows: (() => {
          // Persisted tiers first (in their stored order), then any standard tier
          // the event does not offer, blank — so the admin can still add one.
          const persisted = [...(event.EventTicketTier ?? [])]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((tier) => ({
              key: tier.key,
              label: tier.label,
              priceDollars: String(tier.priceCents / 100),
              // ⚠️ localDateFromKey, NOT new Date(): a @db.Date reads back at UTC
              // midnight, which renders as the PREVIOUS day west of UTC.
              hasEndDate: tier.availableUntil !== null,
              availableUntil: tier.availableUntil
                ? localDateFromKey(tier.availableUntil)
                : null,
            }));
          const seen = new Set(persisted.map((r) => r.key));
          return [
            ...persisted,
            ...emptyTierRows(schoolName).filter((r) => !seen.has(r.key)),
          ];
        })(),
        pricingStructureId: null,
        checkoutWording: event.checkoutWording ?? '',
        // null = the school setting, 0 = none, n = custom (the column's own three meanings); a mode only with a custom
        // amount, the server's rule (review M1).
        ...(() => {
          const fee = bookingFeeChoiceOf(event.bookingFeeCentsOverride, event.bookingFeeModeOverride);
          return { bookingFeeChoice: fee.choice, bookingFeeDollars: fee.dollars, bookingFeeCustomMode: fee.customMode };
        })(),
        requiresApproval: event.requiresApproval,
        featuredImage: event.featuredImage ?? '',
        announcementScheduledFor: event.announcementScheduledFor
          ? new Date(event.announcementScheduledFor)
          : null,
      });
      setVenue({
        locationId: event.locationId ?? null,
        roomId: event.roomId ?? null,
        venueId: event.venueId ?? null,
        venueName: event.venueName ?? null,
      });
      setTargetFamilies(event.targetFamilies ?? []);
      setTargetClasses(event.targetClasses ?? []);
      setTargetAgeGroups(event.targetAgeGroups ?? []);
      setTargetProgramLevels(event.targetProgramLevels ?? []);
    }
  }, [event]);

  const createMutation = useCreateEvent();
  const updateMutation = useUpdateEvent();

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /** Replace the matrix with a structure's defaults. D3: replace, never merge. */
  const seedFromStructure = (structureId: string) => {
    const structure = (pricingStructures ?? []).find((st) => st.id === structureId);
    if (!structure) return;
    setForm((prev) => ({
      ...prev,
      tierRows: rowsFromStructure(structure, schoolName),
      pricingStructureId: structureId,
    }));
  };

  /**
   * Picking a structure REPLACES every row. If the admin has already typed a
   * price, confirm first — otherwise a stray click silently discards their work.
   * ⚠️ Cancel must leave the rows AND the Select untouched (G-M5).
   */
  const applyStructure = (structureId: string) => {
    if (!structureId) return;
    // ⚠️ Dates count as work too. The dialog promises to protect "every ticket
    // price and end date", so a date-only row must raise it — otherwise that
    // promise is false and the date is discarded silently (review MED-1).
    const hasTypedWork = form.tierRows.some(
      (r) => r.priceDollars.trim() !== '' || r.hasEndDate
    );
    if (hasTypedWork) {
      setPendingStructureId(structureId);
      return;
    }
    seedFromStructure(structureId);
  };

  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset the input so the same file can be re-selected if needed
    e.target.value = '';
    setIsUploadingImage(true);
    try {
      const result = await eventApi.uploadFeatureImage(file);
      setField('featuredImage', result.url);
      enqueueSnackbar('Image uploaded', { variant: 'success' });
    } catch {
      enqueueSnackbar('Image upload failed. Please try again.', { variant: 'error' });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handlePixabaySelect = (url: string) => {
    setField('featuredImage', url);
    setShowPixabayPicker(false);
    enqueueSnackbar('Image selected', { variant: 'success' });
  };

  const validate = (): boolean => {
    if (!form.title.trim()) {
      setValidationError('Title is required.');
      return false;
    }
    if (!form.startDateTime) {
      setValidationError('Start date/time is required.');
      return false;
    }
    if (!form.endDateTime) {
      setValidationError('End date/time is required.');
      return false;
    }
    if (form.endDateTime <= form.startDateTime) {
      setValidationError('End date/time must be after start date/time.');
      return false;
    }
    if (!form.isFree) {
      const priced = form.tierRows.filter((r) => {
        const v = r.priceDollars;
        return v.trim() !== '' && parseFloat(v) > 0;
      });
      if (priced.length === 0) {
        // A paid event must have at least one priced tier. We block the save here
        // (stricter than the backend, which only blocks at publish) so the user
        // can't create a paid event that would silently fail to publish later.
        setValidationError('Add a price to at least one ticket tier for a paid event.');
        return false;
      }
      const invalid = form.tierRows.some((r) => {
        const v = r.priceDollars;
        return v.trim() !== '' && (isNaN(parseFloat(v)) || parseFloat(v) < 0);
      });
      if (invalid) {
        setValidationError('Please enter valid ticket prices (numbers ≥ 0).');
        return false;
      }
    }
    if (!form.isFree && form.bookingFeeChoice === 'CUSTOM') {
      const feeError = bookingFeeAmountError(form.bookingFeeDollars);
      if (feeError) {
        setValidationError(`Booking fee: ${feeError}.`);
        return false;
      }
    }
    if (form.visibility === 'SPECIFIC_FAMILIES' && targetFamilies.length === 0) {
      setValidationError('Please select at least one family.');
      return false;
    }
    if (form.visibility === 'SPECIFIC_CLASSES' && targetClasses.length === 0) {
      setValidationError('Please select at least one class.');
      return false;
    }
    if (form.visibility === 'SPECIFIC_AGE_GROUPS' && targetAgeGroups.length === 0) {
      setValidationError('Please select at least one age group.');
      return false;
    }
    if (form.visibility === 'SPECIFIC_PROGRAM_LEVELS' && targetProgramLevels.length === 0) {
      setValidationError('Please select at least one program level.');
      return false;
    }
    setValidationError(null);
    return true;
  };

  const buildPayload = (): CreateEventData => ({
    title: form.title.trim(),
    description: form.description.trim() || undefined,
    type: form.type,
    visibility: form.visibility,
    startDateTime: form.startDateTime!.toISOString(),
    endDateTime: form.endDateTime!.toISOString(),
    locationId: venue.locationId,
    roomId: venue.roomId,
    venueId: venue.venueId,
    venueName: venue.venueName,
    capacity: form.capacity ? parseInt(form.capacity, 10) : null,
    registrationDeadline: form.registrationDeadline
      ? form.registrationDeadline.toISOString()
      : null,
    // isPublic is derived server-side from visibility — not sent from the editor.
    isFree: form.isFree,
    // '' (from the event type) travels as null so switching back clears a stored choice.
    checkoutWording: form.checkoutWording === '' ? null : form.checkoutWording,
    // School setting -> (null, null), None -> (0, null), Custom -> (cents, its mode or null). A FREE event sends
    // neither (absent = unchanged on the server): the control is hidden for it and a free event never charges a fee.
    ...bookingFeeOverridesFor({
      isFree: form.isFree,
      choice: form.bookingFeeChoice,
      dollars: form.bookingFeeDollars,
      customMode: form.bookingFeeCustomMode,
    }),
    // Only when a structure was applied this session, and never for a free event (no tiers to label).
    pricingStructureId: !form.isFree && form.pricingStructureId ? form.pricingStructureId : undefined,
    // Free → no tiers. Paid → one tier per row with a valid price > 0. A blank
    // OR a $0 row means "not offered" (matches validate()'s "priced" rule), so a
    // stray 0 can never persist as a free public ticket option.
    tiers: form.isFree
      ? []
      : form.tierRows.flatMap((r) => {
          const v = r.priceDollars;
          if (v.trim() === '') return [];
          const dollars = parseFloat(v);
          // ⚠️ `<= 0`, never `< 0`: a $0 row means "not offered", and letting one
          // through would publish a free public ticket nobody chose to offer.
          if (isNaN(dollars) || dollars <= 0) return [];
          return [
            {
              key: r.key,
              priceCents: Math.round(dollars * 100),
              // ⚠️ toLocalDateString, NOT .toISOString() — the picker holds a LOCAL
              // wall-clock Date, and an ISO instant stores the previous day east of UTC.
              // Only a day the admin actually chose travels: the toggle can be
              // on with no day yet, and that must send null, not a guess.
              availableUntil:
                r.hasEndDate && r.availableUntil
                  ? toLocalDateString(r.availableUntil)
                  : null,
            },
          ];
        }),
    requiresApproval: form.requiresApproval,
    featuredImage: form.featuredImage.trim() || null,
    targetFamilies,
    targetClasses,
    targetAgeGroups,
    targetProgramLevels,
    announcementScheduledFor: form.announcementScheduledFor
      ? form.announcementScheduledFor.toISOString()
      : null,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = buildPayload();

    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id: id!, data: payload });
        // The event page re-reads what the server stored (the save invalidates it), so the office sees the
        // names that were actually saved, not the ones the form held.
        navigate(`/${schoolSlug}/admin/events/${id}`);
      } else {
        const result = await createMutation.mutateAsync(payload);
        navigate(`/${schoolSlug}/admin/events/${result.id}`);
      }
    } catch (error) {
      // L3: the structure applied here was switched off (or removed) meanwhile. Forget it, keep every edit,
      // and say what to do: the Select no longer lists it, so the office could not clear it themselves.
      if (apiErrorCode(error) === 'PRICING_STRUCTURE_UNAVAILABLE') {
        setForm((prev) => ({ ...prev, pricingStructureId: null }));
        setValidationError(
          'The pricing structure you applied is no longer available. Your changes are kept: pick a structure again, or save without one.'
        );
        return;
      }
      throw error;
    }
  };

  const handleBack = () => {
    if (isEditing) {
      navigate(`/${schoolSlug}/admin/events/${id}`);
    } else {
      navigate(`/${schoolSlug}/admin/events`);
    }
  };

  // Derive selected option objects from id arrays (for Autocomplete value prop)
  const selectedFamilyOptions: TargetOption[] = familyOptions.filter((o) =>
    targetFamilies.includes(o.id)
  );
  const selectedClassOptions: TargetOption[] = classOptions.filter((o) =>
    targetClasses.includes(o.id)
  );
  const selectedAgeGroupOptions: TargetOption[] = ageGroupOptions.filter((o) =>
    targetAgeGroups.includes(o.id)
  );
  const selectedProgramLevelOptions: TargetOption[] = programLevelOptions.filter((o) =>
    targetProgramLevels.includes(o.id)
  );

  if (isEditing && isLoadingEvent) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AppLocalizationProvider>
      <Box>
        <PageHeader
          title={isEditing ? 'Edit Event' : 'Create Event'}
          subtitle={isEditing ? `Editing: ${event?.title ?? ''}` : 'Create a new school event'}
          breadcrumbs={[
            { label: 'Events', path: '/admin/events' },
            { label: isEditing ? 'Edit' : 'New' },
          ]}
        />

        <Box component="form" onSubmit={handleSubmit}>
          {validationError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setValidationError(null)}>
              {validationError}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Basic Details */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Basic Details
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Title"
                  value={form.title}
                  onChange={(e) => setField('title', e.target.value)}
                  required
                  fullWidth
                />
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  multiline
                  rows={4}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Event Type</InputLabel>
                  <Select
                    value={form.type}
                    label="Event Type"
                    onChange={(e) => setField('type', e.target.value as EventType)}
                  >
                    {eventTypeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Paper>

            {/* Audience */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Audience
              </Typography>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    value={form.visibility}
                    label="Visibility"
                    onChange={(e) => setField('visibility', e.target.value as EventVisibility)}
                  >
                    {visibilityOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Target pickers — shown when a "specific" visibility is selected */}
                {form.visibility === 'SPECIFIC_FAMILIES' && (
                  <Box>
                    <Autocomplete
                      multiple
                      options={familyOptions}
                      value={selectedFamilyOptions}
                      loading={isLoadingFamilies}
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      onChange={(_, selected) =>
                        setTargetFamilies(selected.map((o) => o.id))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select families"
                          placeholder={selectedFamilyOptions.length === 0 ? 'Search families...' : ''}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.label}
                            size="small"
                          />
                        ))
                      }
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Only the selected families will be able to see this event.
                    </Typography>
                  </Box>
                )}

                {form.visibility === 'SPECIFIC_CLASSES' && (
                  <Box>
                    <Autocomplete
                      multiple
                      options={classOptions}
                      value={selectedClassOptions}
                      loading={isLoadingLessons}
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      onChange={(_, selected) =>
                        setTargetClasses(selected.map((o) => o.id))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select classes"
                          placeholder={selectedClassOptions.length === 0 ? 'Search classes...' : ''}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.label}
                            size="small"
                          />
                        ))
                      }
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Only students enrolled in the selected classes will be able to see this event.
                    </Typography>
                  </Box>
                )}

                {form.visibility === 'SPECIFIC_AGE_GROUPS' && (
                  <Box>
                    <Autocomplete
                      multiple
                      options={ageGroupOptions}
                      value={selectedAgeGroupOptions}
                      loading={isLoadingAgeGroups}
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      onChange={(_, selected) =>
                        setTargetAgeGroups(selected.map((o) => o.id))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select age groups"
                          placeholder={selectedAgeGroupOptions.length === 0 ? 'Search age groups...' : ''}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.label}
                            size="small"
                          />
                        ))
                      }
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Only families with a student in the selected age groups will see this event.
                    </Typography>
                  </Box>
                )}

                {form.visibility === 'SPECIFIC_PROGRAM_LEVELS' && (
                  <Box>
                    <Autocomplete
                      multiple
                      options={programLevelOptions}
                      value={selectedProgramLevelOptions}
                      loading={isLoadingProgramLevels}
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      onChange={(_, selected) =>
                        setTargetProgramLevels(selected.map((o) => o.id))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select program levels"
                          placeholder={selectedProgramLevelOptions.length === 0 ? 'Search program levels...' : ''}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.label}
                            size="small"
                          />
                        ))
                      }
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Only families with a student at the selected program levels will see this event.
                    </Typography>
                  </Box>
                )}

                <Divider />
                <Stack direction="row" spacing={3} flexWrap="wrap">
                  {/* "Visible to public" toggle removed 2026-06-19 — audience is
                      set solely via the Visibility dropdown (an event is public
                      iff visibility = Public), so the two can't contradict. */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.requiresApproval}
                        onChange={(e) => setField('requiresApproval', e.target.checked)}
                      />
                    }
                    label="Requires approval"
                  />
                </Stack>
              </Stack>
            </Paper>

            {/* Date & Time */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Date & Time
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <DateTimePicker
                  label="Start Date & Time *"
                  value={form.startDateTime}
                  onChange={(val) => setField('startDateTime', val)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DateTimePicker
                  label="End Date & Time *"
                  value={form.endDateTime}
                  onChange={(val) => setField('endDateTime', val)}
                  minDateTime={form.startDateTime ?? undefined}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Stack>
              <Box sx={{ mt: 2 }}>
                <DateTimePicker
                  label="Registration Deadline (optional)"
                  value={form.registrationDeadline}
                  onChange={(val) => setField('registrationDeadline', val)}
                  slotProps={{ textField: { fullWidth: false, sx: { minWidth: 280 } } }}
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                {event?.announcementSentAt ? (
                  <Typography variant="body2" color="text.secondary">
                    Announcement sent:{' '}
                    {new Date(event.announcementSentAt).toLocaleString()}
                  </Typography>
                ) : (
                  <DateTimePicker
                    label="Schedule Announcement For (optional)"
                    value={form.announcementScheduledFor}
                    onChange={(val) => setField('announcementScheduledFor', val)}
                    slotProps={{
                      textField: {
                        fullWidth: false,
                        sx: { minWidth: 280 },
                        helperText:
                          'Leave blank to announce immediately on publish.',
                      },
                    }}
                  />
                )}
              </Box>
            </Paper>

            {/* Venue */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Venue
              </Typography>
              <Stack spacing={2}>
                <EventVenueSelector
                  value={venue}
                  onChange={setVenue}
                  onSuggestCapacity={(cap) => {
                    // Pre-fill capacity from the chosen room/venue, but only when
                    // the admin hasn't already typed one (don't clobber input).
                    setForm((prev) =>
                      prev.capacity ? prev : { ...prev, capacity: String(cap) }
                    );
                  }}
                />
                <TextField
                  label="Capacity"
                  type="number"
                  value={form.capacity}
                  onChange={(e) => setField('capacity', e.target.value)}
                  inputProps={{ min: 0 }}
                  sx={{ minWidth: 160 }}
                  placeholder="Unlimited"
                  helperText="Pre-filled from the room/venue; edit to override."
                />
              </Stack>
            </Paper>

            {/* Pricing */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Pricing
              </Typography>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isFree}
                      onChange={(e) => setField('isFree', e.target.checked)}
                    />
                  }
                  label="Free event"
                />

                {!form.isFree && (
                  <Stack spacing={1.5}>
                    <FormControl size="small" sx={{ maxWidth: 320 }}>
                      <InputLabel id="checkout-wording-label">Wording</InputLabel>
                      <Select
                        labelId="checkout-wording-label"
                        label="Wording"
                        value={form.checkoutWording}
                        onChange={(e) => setField('checkoutWording', e.target.value as FormState['checkoutWording'])}
                      >
                        <MenuItem value="">From the event type</MenuItem>
                        <MenuItem value="TICKET">{wordingOptionLabel(eventSettings?.wording.ticket, 'Tickets')}</MenuItem>
                        <MenuItem value="PLACE">{wordingOptionLabel(eventSettings?.wording.place, 'Places')}</MenuItem>
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        What families see on the buy button: a holiday camp books places, anything else buys tickets,
                        unless you choose here. Edit the words under Event Settings.
                      </Typography>
                    </FormControl>
                    <Stack spacing={0.5} sx={{ maxWidth: 520 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
                        <FormControl size="small" sx={{ minWidth: 240 }}>
                          <InputLabel id="booking-fee-mode-label">Booking fee</InputLabel>
                          <Select
                            labelId="booking-fee-mode-label"
                            label="Booking fee"
                            value={form.bookingFeeChoice}
                            onChange={(e) => setField('bookingFeeChoice', e.target.value as FormState['bookingFeeChoice'])}
                          >
                            <MenuItem value="SCHOOL">
                              {eventSettings?.bookingFee?.enabled
                                ? `School setting (${formatCents(eventSettings.bookingFee.amountCents ?? 0)} ${bookingFeeUnitText(eventSettings.bookingFee.mode)})`
                                : 'School setting'}
                            </MenuItem>
                            <MenuItem value="NONE">No booking fee</MenuItem>
                            <MenuItem value="CUSTOM">Custom fee</MenuItem>
                          </Select>
                        </FormControl>
                        {form.bookingFeeChoice === 'CUSTOM' && (
                          <>
                            <TextField
                              label="Fee amount"
                              size="small"
                              value={form.bookingFeeDollars}
                              onChange={(e) => setField('bookingFeeDollars', e.target.value)}
                              error={form.bookingFeeDollars !== '' && !!bookingFeeAmountError(form.bookingFeeDollars)}
                              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                              inputProps={{ inputMode: 'decimal' }}
                              sx={{ maxWidth: 160 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                              <InputLabel id="booking-fee-custom-mode-label">Charged</InputLabel>
                              <Select
                                labelId="booking-fee-custom-mode-label"
                                label="Charged"
                                // A non-empty sentinel: an empty Select value renders blank in MUI.
                                value={form.bookingFeeCustomMode || 'SCHOOL'}
                                onChange={(e) =>
                                  setField('bookingFeeCustomMode', e.target.value === 'SCHOOL' ? '' : (e.target.value as BookingFeeMode))
                                }
                              >
                                <MenuItem value="SCHOOL">
                                  {/* No unit until the settings are known (loading or failed): never a guess. */}
                                  {eventSettings?.bookingFee
                                    ? `Like the school (${bookingFeeUnitText(eventSettings.bookingFee.mode)})`
                                    : 'Like the school'}
                                </MenuItem>
                                <MenuItem value="PER_TICKET">Per ticket</MenuItem>
                                <MenuItem value="PER_BOOKING">Per booking</MenuItem>
                              </Select>
                            </FormControl>
                          </>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {eventSettings && !eventSettings.bookingFee?.enabled
                          ? 'The school booking fee is switched off under Event Settings, so no booking fee is charged on any event, whatever you choose here.'
                          : 'A flat fee, per paid ticket or once per booking, shown to families before they pay. Set the school setting under Event Settings.'}
                      </Typography>
                    </Stack>
                    {(pricingStructures ?? []).filter((st) => st.isActive).length > 0 && (
                      <FormControl size="small" sx={{ maxWidth: 320 }}>
                        <InputLabel id="pricing-structure-label">
                          Start from a pricing structure
                        </InputLabel>
                        <Select
                          labelId="pricing-structure-label"
                          label="Start from a pricing structure"
                          value={form.pricingStructureId ?? ''}
                          onChange={(e) => applyStructure(e.target.value as string)}
                        >
                          {(pricingStructures ?? [])
                            .filter((st) => st.isActive)
                            .map((st) => (
                              <MenuItem key={st.id} value={st.id}>
                                {st.name}
                                {st.isDefault ? ' (default)' : ''}
                              </MenuItem>
                            ))}
                        </Select>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: 'block' }}
                        >
                          Fills in prices from a saved template. You can still edit
                          any tier below, and this event keeps its own prices even
                          if the template changes later.
                        </Typography>
                      </FormControl>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Enter a price for each ticket tier you offer. Leave a tier
                      blank if you don’t offer it. A tier with an end date stops
                      selling after that day — the last day is included.
                    </Typography>
                    <Stack spacing={1.5}>
                      {form.tierRows.map((row, index) => (
                        <Stack
                          key={row.key}
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'stretch', sm: 'center' }}
                          role="group"
                          // ⚠️ POSITIONAL, never the tier label — not even as a
                          // substring. A group named "<label> pricing" still
                          // matches an unanchored getByLabelText(/<label>/i),
                          // making every school-named tier query ambiguous.
                          aria-label={`Ticket tier ${index + 1}`}
                        >
                        <TextField
                          label={row.label}
                          type="number"
                          size="small"
                          value={row.priceDollars}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              tierRows: prev.tierRows.map((r, i) =>
                                i === index ? { ...r, priceDollars: e.target.value } : r
                              ),
                            }))
                          }
                          inputProps={{ min: 0, step: '0.01' }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">$</InputAdornment>
                            ),
                          }}
                          // A price is a short value: size it like the sibling
                          // Capacity field (minWidth 160), never `flex: 1`, which
                          // stretched it across the whole row.
                          sx={{ minWidth: 160, maxWidth: 200 }}
                        />
                        {row.hasEndDate && (
                          <DatePicker
                            label="On sale until"
                            value={row.availableUntil}
                            onChange={(d) =>
                              setForm((prev) => ({
                                ...prev,
                                tierRows: prev.tierRows.map((r, i) =>
                                  i === index ? { ...r, availableUntil: d } : r
                                ),
                              }))
                            }
                            format="dd/MM/yyyy"
                            slotProps={{
                              textField: {
                                size: 'small',
                                sx: { flex: 1 },
                                helperText: 'Inclusive — sells through this day',
                              },
                            }}
                          />
                        )}
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Paper>

            {/* Featured Image */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Featured Image
              </Typography>

              {/* Upload + Pixabay buttons */}
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleImageFileChange}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    isUploadingImage ? (
                      <CircularProgress size={14} />
                    ) : (
                      <UploadIcon fontSize="small" />
                    )
                  }
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingImage}
                >
                  Upload image
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PixabayIcon fontSize="small" />}
                  onClick={() => setShowPixabayPicker((v) => !v)}
                  disabled={isUploadingImage}
                >
                  {showPixabayPicker ? 'Close Pixabay' : 'Choose from Pixabay'}
                </Button>
              </Stack>

              {/* Pixabay picker — collapses when closed */}
              <Collapse in={showPixabayPicker}>
                <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <EventPixabayPicker onSelect={handlePixabaySelect} />
                </Box>
              </Collapse>

              {/* Chosen image preview + remove (no raw URL shown) */}
              {form.featuredImage ? (
                <Box sx={{ position: 'relative', display: 'inline-block', mt: 1 }}>
                  <Box
                    component="img"
                    src={form.featuredImage}
                    alt="Featured image preview"
                    sx={{
                      display: 'block',
                      maxHeight: 160,
                      maxWidth: '100%',
                      objectFit: 'cover',
                      borderRadius: 1,
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setField('featuredImage', '')}
                    sx={{ mt: 1 }}
                  >
                    Remove image
                  </Button>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No image selected. Upload one or choose from Pixabay above.
                </Typography>
              )}
            </Paper>

            {/* Actions */}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<BackIcon />}
                onClick={handleBack}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                disabled={isSaving}
              >
                {isEditing ? 'Save Changes' : 'Create Event'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
      <ConfirmDialog
        open={pendingStructureId !== null}
        title="Replace the current prices?"
        message={
          `Using "${
            (pricingStructures ?? []).find((st) => st.id === pendingStructureId)?.name ??
            'this structure'
          }" replaces every ticket price and end date in this event with that ` +
          `structure's defaults. The prices currently set here will be lost. ` +
          `Ticket types that have already sold keep their current names.`
        }
        confirmLabel="Replace prices"
        confirmColor="warning"
        onConfirm={() => {
          if (pendingStructureId) seedFromStructure(pendingStructureId);
          setPendingStructureId(null);
        }}
        onCancel={() => setPendingStructureId(null)}
      />
    </AppLocalizationProvider>
  );
}
